import { cn } from '@/lib/utils';
import { getDefaultClassNames } from 'react-day-picker';
import { buttonVariants } from '@/components/ui/button';

const defaultClassNames = getDefaultClassNames();

const baseClasses = {
	...defaultClassNames,
	root: cn('w-fit', defaultClassNames.root),
	months: cn(
		'relative flex flex-col gap-4 md:flex-row',
		defaultClassNames.months
	),
	month: cn('flex w-full flex-col gap-4', defaultClassNames.month),
	nav: cn(
		'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1',
		defaultClassNames.nav
	),
	button_previous: cn(
		buttonVariants({ variant: 'ghost' }),
		'h-[--cell-size] w-[--cell-size] select-none p-0 aria-disabled:opacity-50',
		defaultClassNames.button_previous
	),
	button_next: cn(
		buttonVariants({ variant: 'ghost' }),
		'h-[--cell-size] w-[--cell-size] select-none p-0 aria-disabled:opacity-50',
		defaultClassNames.button_next
	),
	month_caption: cn(
		'flex h-[--cell-size] w-full items-center justify-center px-[--cell-size]',
		defaultClassNames.month_caption
	),
	dropdowns: cn(
		'flex h-[--cell-size] w-full items-center justify-center gap-1.5 text-sm font-medium',
		defaultClassNames.dropdowns
	),
	dropdown_root: cn(
		'has-focus:border-ring border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] relative rounded-md border',
		defaultClassNames.dropdown_root
	),
	dropdown: cn('absolute inset-0 opacity-0', defaultClassNames.dropdown),
	caption_label: cn(
		'select-none font-medium',
		'text-sm',
		defaultClassNames.caption_label
	),
	table: 'w-full border-collapse',
	weekdays: cn('flex', defaultClassNames.weekdays),
	weekday: cn(
		'text-muted-foreground flex-1 select-none rounded-md text-[0.8rem] font-normal',
		defaultClassNames.weekday
	),
	week: cn('mt-2 flex w-full', defaultClassNames.week),
	week_number_header: cn(
		'w-[--cell-size] select-none',
		defaultClassNames.week_number_header
	),
	week_number: cn(
		'text-muted-foreground select-none text-[0.8rem]',
		defaultClassNames.week_number
	),
	day: cn(
		'group/day relative aspect-square h-full w-full select-none p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md',
		defaultClassNames.day
	),
	range_start: cn(
		'bg-accent rounded-l-md',
		defaultClassNames.range_start
	),
	range_middle: cn('rounded-none', defaultClassNames.range_middle),
	range_end: cn('bg-accent rounded-r-md', defaultClassNames.range_end),
	today: cn(
		'bg-accent text-accent-foreground rounded-md data-[selected=true]:rounded-none',
		defaultClassNames.today
	),
	outside: cn(
		'text-muted-foreground aria-selected:text-muted-foreground',
		defaultClassNames.outside
	),
	disabled: cn(
		'text-muted-foreground opacity-50',
		defaultClassNames.disabled
	),
	hidden: cn('invisible', defaultClassNames.hidden)
};


const OVERRIDABLE_PREFIXES = [
	'gap', 'p', 'pt', 'pr', 'pb', 'pl', 'px', 'py',
	'm', 'mt', 'mr', 'mb', 'ml', 'mx', 'my',
	'h', 'w', 'min-h', 'min-w', 'max-h', 'max-w',
	'text', 'bg', 'border', 'rounded',
	'justify', 'items', 'flex', 'grid',
	'shadow', 'font', 'leading', 'tracking'
];

const getPrefix = (cls: string): string =>
	OVERRIDABLE_PREFIXES.find(
		(prefix) => cls === prefix || cls.startsWith(`${prefix}-`)
	) ?? '';

/**
 * Merges two class strings, replacing overlapping utility groups from the base with the override.
 */
function mergeClassNames(base: string, override: string): string {
	const baseClasses = base.trim().split(/\s+/);
	const overrideClasses = override.trim().split(/\s+/);

	const final: string[] = [];

	const seenPrefixes = new Set<string>();
	const overrideMap = new Map<string, string>();

	// Step 1: add all override classes first
	for (const cls of overrideClasses) {
		const prefix = getPrefix(cls);
		if (prefix) {
			overrideMap.set(prefix, cls);
			seenPrefixes.add(prefix);
		}
		final.push(cls);
	}

	// Step 2: add non-conflicting base classes
	for (const cls of baseClasses) {
		const prefix = getPrefix(cls);
		if (!prefix || !seenPrefixes.has(prefix)) {
			final.push(cls);
		}
	}

	return final.join(' ');
}

/**
 * Merges two style objects, preserving the base and overriding class segments selectively.
 */
export function extendStyles(
	override: Record<string, string>
): Record<string, string> {
	const result: Record<string, string> = { ...baseClasses };

	for (const key of Object.keys(override)) {
		result[key] = mergeClassNames(baseClasses[key] ?? '', override[key]);
	}
	return result;
}
